/**
 * 퇴사 타임라인의 날짜 규칙. 전부 순수 함수 — 브라우저 API·서버 없이 계산만 한다.
 * ([계획](../../../../docs/ideas-2026-08/plans/resignation-timeline.md) · 역할은 트래픽 자산,
 * 변동비 0)
 *
 * 여기 적는 기한은 **법령이 날짜를 확정해 주는 것만 하드 날짜**로 내고, 개인 사정(고지 시점
 * 등)에 따라 달라지는 것은 날짜 없이 조건 안내로 낸다 — 확정 판정은 각 기관 몫이라는 게
 * 이 페이지의 비목표다. 규칙 출처는 카드마다 기관 홈 링크로 남긴다. 깊은 링크는 쓰지
 * 않는다 — 이 저장소는 죽은 딥링크로 스토어 반려까지 겪었다.
 */

export interface ResignationInput {
  /** 이직일(마지막 근무일), YYYY-MM-DD */
  leaveDate: string
  /** 자발적 퇴사(스스로 사직) 여부 — 실업급여 카드가 조건부로 갈린다 */
  voluntary: boolean
  /** 고용보험 전체 피보험기간(개월) — 소정급여일수 표의 축 */
  insuredMonths: number
  /** 이직일 현재 만 나이 — 소정급여일수 표의 다른 축 */
  age: number
  /** 퇴직급여(퇴직금·DB·DC) 대상 여부 (계속근로 1년 이상) */
  hasSeverance: boolean
}

export interface TimelineCard {
  id: string
  title: string
  /** yes: 해당 · conditional: 조건 충족 시 · no: 해당 없음 */
  applies: 'yes' | 'conditional' | 'no'
  /** 하드 마감일 (있을 때만) */
  date?: string
  /** 날짜 앞에 붙는 성격 표기 — "신청 마감"인지 "권리 소멸"인지 */
  dateKind?: 'deadline' | 'expires'
  lines: string[]
  /**
   * 근거 기관. **라벨에 한국어 기관명을 남긴다** — 화면은 영어지만 링크가 가는 곳은
   * 한국어 사이트이고, 상담 전화를 걸 때 찾는 이름도 그쪽이다
   */
  source: { label: string; url: string }
}

/* ── 날짜 산술 (UTC 고정 — 로컬 시간대가 끼면 자정 경계에서 하루가 밀린다) ── */

function parse(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function addDays(isoDate: string, days: number): string {
  const d = parse(isoDate)
  d.setUTCDate(d.getUTCDate() + days)
  return iso(d)
}

/** 달력 개월 더하기. 말일이 넘치면 그 달 말일로 붙인다 (1/31 + 1개월 → 2/28) */
export function addMonths(isoDate: string, months: number): string {
  const d = parse(isoDate)
  const day = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + months)
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, last))
  return iso(d)
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((parse(toIso).getTime() - parse(fromIso).getTime()) / 86_400_000)
}

/* ── 소정급여일수 (고용보험법 별표1, 2019-10-01 이후 이직) ── */

/**
 * 행: 이직일 현재 연령 50세 미만 / 50세 이상·장애인, 열: 피보험기간.
 * 이 표가 바뀌면 법 개정이 있었던 것이다 — 값을 고치기 전에 원문을 확인한다.
 */
export function benefitDays(age: number, insuredMonths: number): number {
  const senior = age >= 50
  if (insuredMonths < 12) return 120
  if (insuredMonths < 36) return senior ? 180 : 150
  if (insuredMonths < 60) return senior ? 210 : 180
  if (insuredMonths < 120) return senior ? 240 : 210
  return senior ? 270 : 240
}

/* ── 카드 생성 ── */

export function buildTimeline(input: ResignationInput): TimelineCard[] {
  const { leaveDate, voluntary, insuredMonths, age, hasSeverance } = input
  const cards: TimelineCard[] = []

  // 1. 실업급여 — 수급기간은 이직일 다음 날부터 12개월 (고용보험법 제48조)
  const claimExpiry = addMonths(leaveDate, 12)
  const days = benefitDays(age, insuredMonths)
  const eligibleByHistory = insuredMonths >= 6 // 피보험 단위기간 180일 요건의 근사 안내
  cards.push({
    id: 'unemployment',
    title: 'Unemployment benefit (job-seeking allowance)',
    applies: voluntary ? 'conditional' : eligibleByHistory ? 'yes' : 'conditional',
    date: claimExpiry,
    dateKind: 'expires',
    lines: [
      'The claim window runs for 12 months from the day after your last working day. Once it passes, any unused benefit days are gone.',
      `On your figures the maximum is ${days} benefit days (age ${age}, insured period ${formatMonths(insuredMonths)}). The later you claim, the more likely you are to run out of window before you run out of days — claiming right after leaving is the safe move.`,
      voluntary
        ? 'Leaving voluntarily is, as a rule, outside the scheme. It can still qualify where the reason for leaving is recognised as justified — unpaid wages, an impossible commute, and so on — and the employment centre decides that.'
        : 'Whether you meet the conditions — 180 or more insured days within the 18 months before leaving, among others — is confirmed by the employment centre.',
    ],
    source: {
      label: 'Work24 / 고용24 (claim unemployment benefit)',
      url: 'https://www.work24.go.kr',
    },
  })

  // 2. 퇴직금 — 지급 사유 발생일부터 14일 이내 (근로자퇴직급여 보장법 제9조)
  cards.push({
    id: 'severance',
    title: 'Severance payment',
    applies: hasSeverance ? 'yes' : 'no',
    ...(hasSeverance ? { date: addDays(leaveDate, 14), dateKind: 'deadline' as const } : {}),
    lines: hasSeverance
      ? [
          'It must be paid within 14 days of your last working day. Only an agreement between the two of you can extend that.',
          'Past the deadline, interest accrues at 20% a year, and you can file an unpaid-wages complaint with the Ministry of Employment and Labor.',
        ]
      : [
          'With less than one year of continuous service, or under 15 hours a week, you may fall outside the severance scheme.',
        ],
    source: { label: 'Ministry of Employment and Labor / 고용노동부', url: 'https://www.moel.go.kr' },
  })

  // 3. IRP 이전 — 55세 미만 퇴직급여는 IRP 지급이 원칙 (근로자퇴직급여 보장법 제17조)
  cards.push({
    id: 'irp',
    title: 'Receiving severance into an IRP',
    applies: hasSeverance ? (age < 55 ? 'yes' : 'conditional') : 'no',
    lines: hasSeverance
      ? age < 55
        ? [
            'Below the age of 55, severance is as a rule paid into an IRP (individual retirement pension) account — payment only proceeds once you give your employer the account details.',
            'Exceptions, such as a lump sum of ₩3,000,000 or less, can be paid into an ordinary account.',
            'Taken through an IRP, retirement income tax is deferred until you withdraw; withdrawing the wrong way loses that benefit.',
          ]
        : [
            'From the age of 55 there is no obligation to route it through an IRP, and you may take it into an ordinary account. Which is better for tax depends on how you draw it.',
          ]
      : ['With no severance, this does not apply.'],
    source: {
      label: 'Ministry of Employment and Labor / 고용노동부 (retirement pensions)',
      url: 'https://www.moel.go.kr',
    },
  })

  // 4. 건강보험 임의계속가입 — 최초 지역보험료 고지분 납부기한부터 2개월 이내 (국민건강보험법 제110조)
  cards.push({
    id: 'nhis',
    title: 'Continued employee health insurance',
    applies: 'conditional',
    lines: [
      'On leaving, you move to the regional health insurance category, where the premium can go up. The continued-coverage scheme keeps you at roughly the employee-scheme premium for up to 36 months.',
      'The deadline is two months from the due date of the first premium billed to you as a regional subscriber. Bills arrive at different times for different people, so take the exact date from your own bill — billing usually starts the month after you leave.',
      'You qualify if you were on the employee scheme for at least one year within the 18 months before leaving.',
    ],
    source: {
      label: 'National Health Insurance Service / 국민건강보험공단',
      url: 'https://www.nhis.or.kr',
    },
  })

  // 5. 국민연금 — 소득 중단 시 납부예외 신청 가능 (국민연금법 제91조)
  cards.push({
    id: 'nps',
    title: 'National Pension: exemption or regional membership',
    applies: 'conditional',
    lines: [
      'With no income after leaving, you can apply for a payment exemption and stop contributing. That period does not count towards your contribution history.',
      'When the notice about moving to regional membership arrives, you choose between the exemption and voluntary contributions. It feeds straight into what you receive later, so it is worth talking to the service first.',
    ],
    source: { label: 'National Pension Service / 국민연금공단', url: 'https://www.nps.or.kr' },
  })

  // 6. 중도퇴사 연말정산 — 재취업 없으면 다음 해 5월 종합소득세 확정신고 (소득세법 제70조)
  const may31 = `${Number(leaveDate.slice(0, 4)) + 1}-05-31`
  cards.push({
    id: 'tax',
    title: 'Year-end tax settlement after leaving mid-year',
    applies: 'yes',
    date: may31,
    dateKind: 'deadline',
    lines: [
      'On your way out, the employer runs a short settlement that applies the basic deductions only — anything missed can still be claimed back.',
      'Start a new job within the year and the new employer folds your previous income into its year-end settlement. Do not, and you claim the refund yourself in the comprehensive income tax return the following May.',
    ],
    source: { label: 'Hometax / 국세청 홈택스', url: 'https://www.hometax.go.kr' },
  })

  return cards
}

function formatMonths(months: number): string {
  if (months < 12) return `${months} months`
  const y = Math.floor(months / 12)
  const m = months % 12
  const years = `${y} year${y === 1 ? '' : 's'}`
  return m === 0 ? years : `${years} ${m} months`
}

/* ── .ics 내보내기 — 앱 설치 없이 사용자 캘린더로 알림을 넘긴다 ── */

/**
 * 날짜 있는 카드만 종일 일정으로 담는다. 마감 당일 + (7일 이상 남았으면) D-7 사전 알림을
 * 별도 일정으로 넣는다 — VALARM 은 캘린더 앱마다 가져오기 지원이 갈려서 일정 두 개가
 * 더 확실하다.
 */
export function buildIcs(cards: TimelineCard[], todayIso: string): string {
  const events: string[] = []
  for (const card of cards) {
    if (card.date === undefined || card.applies === 'no') continue
    const label = card.dateKind ?? 'deadline'
    events.push(
      icsEvent(
        `${card.id}-due`,
        card.date,
        `[Resignation Timeline] ${card.title} — ${label}`,
        card.lines[0] ?? '',
      ),
    )
    if (daysBetween(todayIso, card.date) >= 7) {
      events.push(
        icsEvent(
          `${card.id}-d7`,
          addDays(card.date, -7),
          `[Resignation Timeline] ${card.title} — ${label} in 7 days`,
          card.lines[0] ?? '',
        ),
      )
    }
  }
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//cottonray.com//resignation-timeline//EN',
    'CALSCALE:GREGORIAN',
    ...events,
    'END:VCALENDAR',
    '',
  ].join('\r\n')
}

function icsEvent(uid: string, dateIso: string, summary: string, description: string): string {
  const ymd = dateIso.replaceAll('-', '')
  return [
    'BEGIN:VEVENT',
    `UID:${uid}-${ymd}@cottonray.com`,
    `DTSTAMP:${ymd}T000000Z`,
    `DTSTART;VALUE=DATE:${ymd}`,
    `SUMMARY:${icsEscape(summary)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    'END:VEVENT',
  ].join('\r\n')
}

function icsEscape(text: string): string {
  return text.replaceAll('\\', '\\\\').replaceAll(';', '\\;').replaceAll(',', '\\,').replaceAll('\n', '\\n')
}
